import {
  IsNumber,
  IsObject,
  IsOptional,
  IsString,
  ValidateNested,
} from 'class-validator';

/**
 * cell 의 텍스트 정렬 속성.
 * {@link ExcelStyleBaseCellDto}
 * @public
 */
export type TextAlignment = {
  vertical?: 'top' | 'middle' | 'bottom' | 'distributed' | 'justify' | string;
  horizontal?:
    | 'left'
    | 'center'
    | 'right'
    | 'fill'
    | 'justify'
    | 'centerContinuous'
    | 'distributed';
  wrapText?: boolean;
};
/**
 * cell 의 테두리 속성 세부.
 * {@link ExcelStyleBaseCellDto}
 * @public
 */
export type BorderDetailStyle = {
  style?: 'thin' | 'dotted' | 'hair' | 'medium' | 'dashDot' | 'double' | string;
  color?: {argb: string};
};
/**
 * cell 의 테두리 속성.
 * {@link ExcelStyleBaseCellDto}
 * @public
 */
export type BorderStyle = {
  top?: BorderDetailStyle;
  bottom?: BorderDetailStyle;
  left?: BorderDetailStyle;
  right?: BorderDetailStyle;
};
/**
 * 기본 텍스트 Cell
 * ```
 * {
 *    mergeRange: 'AA1:AD1',
 *    cellValue: "cell value",
 *    fontSize: 10,
 *    fontColor: ffffff,
 *    fontBold: true,
 *    alignment: {
 *        vertical: "middle",
 *        horizontal: "left",
 *        wrapText: true
 *    }
 * }
 * ```
 *
 * {@link ExcelStyleGroupTitleAndTextValueDto}
 * {@link ExcelStyleGroupListTitleAndTextValueDto}
 * {@link ExcelStyleGroupListTitleImgValueTextValueDto}
 * @public
 */
export class ExcelStyleBaseCellDto {
  /**
   * 병합 범위.
   */
  @IsString()
  mergeRange: string;
  /**
   * 셀의 값.
   */
  @IsString()
  @IsOptional()
  cellValue?: string;
  /**
   * 셀의 Font size.
   * default=8
   */
  @IsOptional()
  @IsString()
  fontSize?: number;
  /**
   * 셀의 Font Color. hex code(exclude #)
   * default=000000
   */
  @IsOptional()
  @IsString()
  fontColor?: string;
  /**
   * 셀의 Font bold 여부.
   */
  @IsOptional()
  @IsString()
  fontBold?: boolean;
  /**
   * 셀의 text alignment.
   * default 는 아래와 같다.
   * ```
   * {
   *   vertical: "middle",
   *   horizontal: "center",
   *   wrapText: true
   * }
   * ```
   */
  @IsOptional()
  @IsObject()
  alignment?: TextAlignment;
  /**
   * 셀의 row height.
   */
  @IsOptional()
  @IsNumber()
  rowHeight?: number;
  /**
   * 셀의 테두리.
   */
  @IsOptional()
  @IsObject()
  border?: BorderStyle;
}

/**
 * 기본 이미지 셀.
 * ```
 * {
 *    mergeRange: 'AA1:AD1',
 *    img: 'asdf.png',
 *    rowHeight: 40
 * }
 * ```
 *
 * {@link ExcelStyleGroupTitleAndImgValueDto}
 * {@link ExcelStyleGroupListTitleImgValueTextValueDto}
 * @public
 */
export class ExcelStyleImgCellDto {
  /**
   * 병합 범위.
   */
  @IsString()
  mergeRange: string;
  /**
   * 셀의 값.
   */
  img?: string;
  /**
   * 셀의 row height.
   */
  @IsOptional()
  @IsNumber()
  rowHeight?: number;
}

/**
 * 셀의 배경색.
 * ```
 * {
 *    mergeRange: 'AA1:AD1',
 *    cellValue: "cell value",
 *    fontSize: 10,
 *    fontColor: ffffff,
 *    fontBold: true,
 *    alignment: {
 *        vertical: "middle",
 *        horizontal: "left",
 *        wrapText: true
 *    },
 *    fillColor: '1900ff'
 * }
 * ```
 *
 * {@link ExcelStyleGroupTitleAndImgValueDto}
 * {@link ExcelStyleGroupTitleAndTextValueDto}
 * {@link ExcelStyleGroupListTitleAndTextValueDto}
 * {@link ExcelStyleGroupListTitleImgValueTextValueDto}
 * @public
 */
export class ExcelStyleFillCellDto extends ExcelStyleBaseCellDto {
  /**
   * 셀의 배경색 Color. hex code(exclude #)
   * default=000000
   */
  @IsString()
  fillColor?: string;
}

/**
 * 배경색 있는 셀 1개 + 이미지 셀 1개
 * ```
 *   {
 *     categoryCell: {
 *       mergeRange: 'AA1:AD1',
 *       cellValue: param.signingTitleList[0].name,
 *       fillColor: 'd8d8d8',
 *     },
 *     imgValueCell: {
 *       mergeRange: 'AA2:AD2',
 *       img: param.signingTitleList[0].signature,
 *       rowHeight: pixelToPtHeight(46),
 *     },
 *   },
 * ```
 * @public
 */
export class ExcelStyleGroupTitleAndImgValueDto {
  /**
   * 카테고리 셀.
   */
  @ValidateNested()
  categoryCell: ExcelStyleFillCellDto;
  /**
   * 카테고리 이미지 셀.
   */
  @ValidateNested()
  imgValueCell: ExcelStyleImgCellDto;
}

/**
 * 배경색 있는 셀 1개 + 텍스트 셀 1개
 * ```
 *   {
 *     categoryCell: {
 *       mergeRange: 'A3:D3',
 *       cellValue: defaultName[locale].tongPlaceName,
 *       fillColor: 'd8d8d8',
 *     },
 *     textValueCell: {
 *       mergeRange: 'E3:L3',
 *       cellValue: param.tongPlaceName,
 *     },
 *   }
 * ```
 * @public
 */
export class ExcelStyleGroupTitleAndTextValueDto {
  /**
   * 카테고리.
   */
  @ValidateNested()
  categoryCell: ExcelStyleFillCellDto;
  /**
   * 값.
   */
  @ValidateNested()
  textValueCell: ExcelStyleBaseCellDto;
}

/**
 * 배경색 있는 셀 list + 텍스트 셀 list
 * ```
 * {
 *   categoryCell: [
 *     {
 *       mergeRange: 'A3:D3',
 *       cellValue: defaultName[locale].tongPlaceName,
 *       fillColor: 'd8d8d8',
 *     },
 *   ],
 *   textValueCell: [
 *     {
 *       mergeRange: 'E3:L3',
 *       cellValue: param.tongPlaceName,
 *     },
 *   ],
 * };
 * ```
 * @public
 */
export class ExcelStyleGroupListTitleAndTextValueDto {
  /**
   * 카테고리 셀 리스트.
   */
  @ValidateNested({each: true})
  categoryCell: ExcelStyleFillCellDto[];
  /**
   * 값 리스트.
   */
  @ValidateNested({each: true})
  textValueCell: ExcelStyleBaseCellDto[];
}

/**
 * 배경색 있는 셀 list + 이미지 셀 list + 텍스트 셀 list
 * ```
 * {
 *   categoryCell: [
 *     {
 *       mergeRange: 'A3:D3',
 *       cellValue: defaultName[locale].tongPlaceName,
 *       fillColor: 'd8d8d8',
 *     },
 *   ],
 *   imgValueCell: [
 *     {
 *       mergeRange: 'E3:L3',
 *       img: 'asdf.png',
 *     },
 *   ],
 *   textValueCell: [
 *     {
 *       mergeRange: 'E3:L3',
 *       cellValue: param.tongPlaceName,
 *     },
 *   ],
 * };
 * ```
 * @public
 */
export class ExcelStyleGroupListTitleImgValueTextValueDto {
  /**
   * 카테고리 셀 리스트.
   */
  @ValidateNested()
  categoryCell: ExcelStyleFillCellDto[];
  /**
   * 이미지 셀 리스트.
   */
  @ValidateNested()
  imgValueCell: ExcelStyleImgCellDto[];
  /**
   * 텍스트 셀 리스트.
   */
  @ValidateNested()
  textValueCell: ExcelStyleBaseCellDto[];
}

/**
 * 배경색 있는 셀 1개 + 이미지 셀 list
 * ```
 *   {
 *     categoryCell: {
 *       mergeRange: 'AA1:AD1',
 *       cellValue: param.signingTitleList[0].name,
 *       fillColor: 'd8d8d8',
 *     },
 *     imgValueCell: [
 *       {
 *        mergeRange: 'AA2:AD2',
 *        img: param.signingTitleList[0].signature,
 *        rowHeight: pixelToPtHeight(46),
 *      },
 *     ],
 *   },
 * ```
 * @public
 */
export class ExcelStyleGroupTitleAndImgValueListDto {
  /**
   * 카테고리 셀.
   */
  @ValidateNested()
  categoryCell: ExcelStyleFillCellDto;
  /**
   * 카테고리 이미지 셀.
   */
  @ValidateNested()
  imgValueCell: ExcelStyleImgCellDto[];
}
